import React, { useEffect, useState } from 'react';
import { useFormContext } from './FormContext';

// Individual option component
export const Option = ({ 
  value, 
  label, 
  render, // Simple string to render
  formatter, // Override function if needed
  children 
}) => {
  // This component doesn't render anything directly
  // It's used to collect option configuration
  return null;
};

// Option group container
export const OptionGroup = ({ 
  id,
  type = 'checkbox', // 'checkbox' or 'radio'
  label,
  formatMultiple = null, // Function to format multiple selections
  children,
  className = '',
  optionsClassName = 'space-y-1' // Allow custom styling for options container
}) => {
  const { registerOptionGroup, updateField, getFieldValue } = useFormContext();
  const [options, setOptions] = useState([]);

  // Extract options from children
  useEffect(() => {
    const extractedOptions = [];
    
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        // Handle MDX-wrapped components and regular Option components
        const isOption = child.type === Option || 
                        child.props?.originalType === Option ||
                        child.props?.mdxType === 'Option';
        
        if (isOption) {
          // Create formatter based on priority: formatter > render > label
          let finalFormatter = child.props.formatter;
          if (!finalFormatter) {
            if (child.props.render) {
              finalFormatter = () => child.props.render;
            } else {
              finalFormatter = () => child.props.label;
            }
          }
          
          extractedOptions.push({
            value: child.props.value,
            label: child.props.label,
            formatter: finalFormatter,
          });
        } else {
          // For predefined option components, check various places where optionConfig might be
          let optionConfig = null;
          
          // Check if optionConfig is passed through props (new approach)
          if (child.props && child.props.optionConfig) {
            optionConfig = child.props.optionConfig;
          }
          // Check if this is an MDX-wrapped component with originalType
          else if (child.props && child.props.originalType && child.props.originalType.optionConfig) {
            optionConfig = child.props.originalType.optionConfig;
          }
          // Direct property on component
          else if (child.type && child.type.optionConfig) {
            optionConfig = child.type.optionConfig;
          }
          // On the render function (for forward refs)
          else if (child.type && child.type.render && child.type.render.optionConfig) {
            optionConfig = child.type.render.optionConfig;
          }
          // Try other possible locations
          else if (child.type && child.type.type && child.type.type.optionConfig) {
            optionConfig = child.type.type.optionConfig;
          }
          
          if (optionConfig) {
            extractedOptions.push(optionConfig);
          }
        }
      }
    });
    setOptions(extractedOptions);
  }, [children, id]);

  // Register the option group
  useEffect(() => {
    if (options.length > 0) {
      registerOptionGroup({
        id,
        type,
        label,
        options,
        formatMultiple,
      });
    }
  }, [id, type, label, options, formatMultiple]); // Remove registerOptionGroup

  // Initialize form value
  useEffect(() => {
    const currentValue = getFieldValue(id);
    if (!currentValue) {
      updateField(id, type === 'checkbox' ? [] : '');
    }
  }, [id, type]); // Remove function dependencies

  const currentValue = getFieldValue(id);

  const handleCheckboxChange = (optionValue) => {
    const newValue = currentValue.includes(optionValue)
      ? currentValue.filter(v => v !== optionValue)
      : [...currentValue, optionValue];
    updateField(id, newValue);
  };

  const handleRadioChange = (optionValue) => {
    updateField(id, optionValue);
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-semibold mb-1">{label}</label>}
      <div className={optionsClassName}>
        {options.map((option) => (
          <label key={option.value} className="flex items-center">
            <input
              type={type === 'checkbox' ? 'checkbox' : 'radio'}
              name={type === 'radio' ? id : undefined}
              value={option.value}
              checked={
                type === 'checkbox' 
                  ? currentValue.includes(option.value)
                  : currentValue === option.value
              }
              onChange={() => 
                type === 'checkbox' 
                  ? handleCheckboxChange(option.value)
                  : handleRadioChange(option.value)
              }
              className="mr-2 h-3 w-3 text-brand-red-3"
            />
            <span className="text-sm">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};